import { Component } from 'react';
import styles from '../editor.module.scss';
import { MotiveComponent } from '../../ui/motive/motive.component';
import { AdSlots } from '../../ui/motive/ad-slots';
import { MotiveLocationTypes } from '../../../utils/request.utils';
import {
    EditState, Tool, Size, isApplied, resetTool, resizeDisplayValues, setResizeWidth, setResizeHeight, setResizeLock, setResizeUnit,
    setResizePercent, setResizeByHeight, setResizeBox, setCropAspect, setCropLock, setCropShape, setCropSize, setCropToPlatformSize, rotateQuarter, setFineAngle, setCornerFill, toggleFlip,
    orientedSize, outputSize, CornerFill, resizeView, cropView, rotateView, flipView, upscaleFactor,
} from '../model/edit-stack';
import { ASPECT_PRESETS, HEIGHT_PRESETS, PERCENT_PRESETS, PLATFORM_GROUPS, platformSizeLabel } from '../presets';
import type { ImageFormat } from '../codecs/types';
import { formatLabel } from '../routing';
import type { CropPolicy } from '../model/batch';

export type SaveFormat = ImageFormat | 'original';
export const SAVE_FORMATS: ImageFormat[] = ['jpg', 'png', 'webp', 'gif', 'bmp', 'tiff', 'ico', 'avif', 'psd', 'ppm'];
export const LOSSY: ImageFormat[] = ['jpg', 'webp', 'avif'];

export interface OutputInfo {
    size: Size;
    format: ImageFormat;          // effective (after fallback)
    extension: string;
    estimate: string;             // "≈ 312 KB"
    delta: { pct: number; up: boolean } | null;
    filename: string;
    note: string | null;          // fallback explanation, e.g. "HEIC can't be written here — saving as JPG."
}

export interface InspectorProps {
    state: EditState;
    tool: Tool;
    onTool: (t: Tool) => void;
    onState: (next: EditState) => void;
    onResetAll: () => void;
    /** Per-tool Reset; the editor also returns the view to Fit. Falls back to a plain state reset. */
    onResetTool?: (t: Tool) => void;
    sourceFormat: ImageFormat;
    originalBytes: number;
    format: SaveFormat;
    onFormat: (f: SaveFormat) => void;
    quality: number;
    onQuality: (q: number) => void;
    output: OutputInfo;
    batchCount: number;
    cropPolicy: CropPolicy;
    onCropPolicy: (p: CropPolicy) => void;
    /** Fine-angle slider held → the stage shows a thirds grid to straighten against (UX review N3). */
    onAngleDragging: (dragging: boolean) => void;
    exporting: { done: number; total: number; label: string } | null;
    onExport: (mode: 'single' | 'zip' | 'each' | 'share') => void;
    /** navigator.share with files is available (phones): show "Share…" so the photo can go to Photos / an app. */
    canShare: boolean;
}

const ICONS: Record<Tool, string> = {
    resize: 'M4 14v6h6M20 10V4h-6M20 4l-7 7M4 20l7-7',
    crop: 'M6 2v14a2 2 0 0 0 2 2h14M2 6h14a2 2 0 0 1 2 2v14',
    rotate: 'M21 12a9 9 0 1 1-3-6.7M21 3v6h-6',
    flip: 'M12 3v18M4 7l5 5-5 5V7zM20 7l-5 5 5 5V7z',
};
const TOOLS: Tool[] = ['resize', 'crop', 'rotate', 'flip'];
const NAMES: Record<Tool, string> = { resize: 'Resize', crop: 'Crop', rotate: 'Rotate', flip: 'Flip' };
const ALPHA_FORMATS: ImageFormat[] = ['png', 'webp', 'avif', 'gif', 'tiff', 'psd', 'ico'];   // can keep a circle's corners transparent
const COMPRESSIBLE: ImageFormat[] = ['jpg', 'png', 'webp', 'gif', 'avif', 'bmp', 'heic', 'tiff']; // /compress-<ext> pages that exist (verified 2026-09-06)

/** The tool switcher. Its own grid area so it sits top-right on desktop and as a thin bar ABOVE the
 *  photo on phones — the first thing a new user sees (UX review M1, 2026-09-06: the sticky sheet used
 *  to cover it on first load). */
export function ToolTabs({ state, tool, onTool }: { state: EditState; tool: Tool; onTool: (t: Tool) => void }) {
    return <div className={styles.tabs} role="tablist" aria-label="Tools">
        {TOOLS.map(t => <button key={t} type="button" role="tab" aria-selected={tool === t} data-applied={isApplied(state, t)} data-testid={`tab-${t}`} onClick={() => onTool(t)}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[t]} /></svg><span>{NAMES[t]}</span>
        </button>)}
    </div>;
}

/** The four panes and the output block. Stateless apart from the phone-only quality toggle: every
 *  edit goes up through onState. */
export class InspectorComponent extends Component<InspectorProps, { showQuality: boolean }> {
    state = { showQuality: false };
    private num(v: string): number | null { const n = parseInt(v, 10); return Number.isFinite(n) && n > 0 ? n : null; }

    render() {
        const { tool } = this.props;
        return <div className={styles.panel}>
            <div className={styles.body}>
                {tool === 'resize' && this.resizePane()}
                {tool === 'crop' && this.cropPane()}
                {tool === 'rotate' && this.rotatePane()}
                {tool === 'flip' && this.flipPane()}
                {/* Inspector void: a 300x250 in the empty band below the controls (desktop only).
                    Reuses the in-content role key + slot -- see AD_PLACEMENT_DESIGN.md 9. */}
                <div className={styles.inspectorAd}>
                    <MotiveComponent
                        data={AdSlots.inContent} containerWidth="300px" containerHeight="250px"
                        adWidth="300px" adHeight="250px" showAds={true}
                        location={MotiveLocationTypes.convertPageInContent} className="rect_med"
                    ></MotiveComponent>
                </div>
            </div>
            {this.outputBlock()}
        </div>;
    }

    private paneHead(t: Tool) {
        return <div className={styles.panehead}><h4>{NAMES[t]}</h4>
            <button type="button" className={styles.link} onClick={() => this.props.onResetTool ? this.props.onResetTool(t) : this.props.onState(resetTool(this.props.state, t))} style={{ visibility: isApplied(this.props.state, t) ? 'visible' : 'hidden' }}>Reset</button>
        </div>;
    }

    // ------------------------------------------------------------ resize
    private resizePane() {
        const { state } = this.props; const rv = resizeView(state); const vals = resizeDisplayValues(state);
        const lock = rv.lockAspect, unit = rv.unit;
        // Growing past the source is allowed (no toggle). The only feedback is one quiet line in the hint
        // slot that already exists, so nothing moves and nothing has to be dismissed (owner, 2026-09-07).
        const up = upscaleFactor(state);
        const beyond = up > 1.02 ? `${up.toFixed(up < 10 ? 1 : 0)}× the original — fine detail will soften.` : '';
        const o = orientedSize(state); const aspect = (o.width / o.height).toFixed(2);
        const onPreset = (v: string) => {
            if (v === 'custom') return;
            const [kind, val] = v.split(':');
            if (kind === 'pct') this.props.onState(setResizePercent(state, +val));
            else if (kind === 'h') this.props.onState(setResizeByHeight(state, +val));
            else if (kind === 'box') { const [w, h] = val.split('x').map(Number); this.props.onState(setResizeBox(state, { width: w, height: h })); }
        };
        // Dimensions first: it is the control most people came for, and on a phone it keeps the W/H
        // inputs above the sticky sheet on load (QA measurement 2026-09-06). Presets follow.
        return <div className={styles.pane}>
            {this.paneHead('resize')}
            <div className={styles.row}>
                <label className={styles.l}>Dimensions</label>
                <div className={styles.field}>
                    <div className={styles.num}><span>W</span><input inputMode="numeric" aria-label="Width" data-testid="resize-w" value={vals.width}
                        onChange={e => { const n = this.num(e.target.value); if (n === null) return; this.props.onState(unit === '%' ? setResizePercent(state, n) : setResizeWidth(state, n)); }} /></div>
                    <button type="button" className={styles.lock} aria-pressed={lock} title={lock ? 'Aspect ratio locked' : 'Aspect ratio free'} aria-label="Keep aspect ratio" onClick={() => this.props.onState(setResizeLock(state, !lock))}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d={lock ? 'M8 11V7a4 4 0 0 1 8 0v4' : 'M8 11V7a4 4 0 0 1 7.5-1.5'} /></svg>
                    </button>
                    <div className={styles.num}><span>H</span><input inputMode="numeric" aria-label="Height" data-testid="resize-h" value={vals.height}
                        onChange={e => { const n = this.num(e.target.value); if (n === null) return; this.props.onState(unit === '%' ? setResizePercent(state, n) : setResizeHeight(state, n)); }} /></div>
                    <select className={styles.select} style={{ width: 'auto' }} aria-label="Unit" value={unit} onChange={e => this.props.onState(setResizeUnit(state, e.target.value as 'px' | '%'))}><option value="px">px</option><option value="%">%</option></select>
                </div>
                <span className={styles.hint} data-testid="resize-hint">{beyond || (lock ? `Locked at ${aspect}:1. Drag a corner of the photo or type a number.` : 'Unlocked: width and height move separately (the image will stretch).')}</span>
            </div>
            <div className={styles.row}>
                <label className={styles.l} htmlFor="ed-preset">Size preset</label>
                <select id="ed-preset" className={styles.select} value="custom" onChange={e => onPreset(e.target.value)}>
                    <option value="custom">Custom</option>
                    <optgroup label="Percent of original">{PERCENT_PRESETS.map(p => <option key={p} value={`pct:${p}`}>{p}%</option>)}</optgroup>
                    <optgroup label="By height">{HEIGHT_PRESETS.map(h => <option key={h} value={`h:${h}`}>{h}p</option>)}</optgroup>
                    {PLATFORM_GROUPS.map(g => <optgroup key={g.name} label={g.name}>{g.sizes.map(s => <option key={s.label} value={`box:${s.width}x${s.height}`}>{platformSizeLabel(g, s)}</option>)}</optgroup>)}
                </select>
            </div>
        </div>;
    }

    // ------------------------------------------------------------ crop
    private cropPane() {
        const { state, batchCount, cropPolicy } = this.props;
        const cv = cropView(state); const c = cv.rect;                       // in the crop base's pixels (additive model)
        const cur = cv.aspect;
        const srcAspect = cv.input.width / cv.input.height;                  // "Original" = the base's own aspect
        const pressed = (v: number | 'original' | null) => v === null ? cur === null : v === 'original' ? cur !== null && Math.abs(cur - srcAspect) < 1e-6 : cur !== null && Math.abs(cur - v) < 1e-6;
        return <div className={styles.pane}>
            {this.paneHead('crop')}
            <div className={styles.row}>
                <div className={styles.lrow}><label className={styles.l}>Aspect ratio</label>
                    {/* lock = hold the box's current shape while dragging (UX review A7); a chip is a specific ratio and so also "locked" */}
                    <button type="button" className={styles.lock} aria-pressed={cur !== null} title={cur !== null ? 'Ratio locked — unlock to drag each edge freely' : 'Ratio free — lock to keep the current shape'} aria-label="Lock crop ratio" data-testid="crop-lock" onClick={() => this.props.onState(setCropLock(state, cur === null))}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="11" width="14" height="10" rx="2" /><path d={cur !== null ? 'M8 11V7a4 4 0 0 1 8 0v4' : 'M8 11V7a4 4 0 0 1 7.5-1.5'} /></svg>
                    </button>
                </div>
                <div className={styles.chips}>
                    {ASPECT_PRESETS.map(p => <button key={p.label} type="button" className={styles.chip} aria-pressed={pressed(p.value)} data-testid={`aspect-${p.label}`} onClick={() => this.props.onState(setCropAspect(state, p.value))}>{p.label}</button>)}
                </div>
            </div>
            <div className={styles.row}>
                <label className={styles.l}>Shape</label>
                <div className={styles.chips}>
                    <button type="button" className={styles.chip} aria-pressed={cv.shape !== 'circle'} data-testid="shape-rect" onClick={() => this.props.onState(setCropShape(state, 'rect'))}>Rectangle</button>
                    {/* circle avatars need transparent corners: switch a no-alpha format to PNG on the way in */}
                    <button type="button" className={styles.chip} aria-pressed={cv.shape === 'circle'} data-testid="shape-circle" onClick={() => { this.props.onState(setCropShape(state, 'circle')); if (!ALPHA_FORMATS.includes(this.props.output.format)) this.props.onFormat('png'); }}>Circle (avatar)</button>
                </div>
                {cv.shape === 'circle' && <span className={styles.hint}>Saved as a square with see-through corners; PNG keeps them transparent.</span>}
            </div>
            <div className={styles.row}>
                <label className={styles.l} htmlFor="ed-platform">Or a platform size</label>
                <select id="ed-platform" className={styles.select} value="none" onChange={e => { const [w, h] = e.target.value.split('x').map(Number); if (w && h) this.props.onState(setCropToPlatformSize(state, { width: w, height: h })); }}>
                    <option value="none">None</option>
                    {PLATFORM_GROUPS.map(g => <optgroup key={g.name} label={g.name}>{g.sizes.map(s => <option key={s.label} value={`${s.width}x${s.height}`}>{platformSizeLabel(g, s)}</option>)}</optgroup>)}
                </select>
            </div>
            <div className={styles.row}>
                <label className={styles.l}>Crop area</label>
                <div className={styles.field}>
                    <div className={styles.num}><span>W</span><input inputMode="numeric" aria-label="Crop width" data-testid="crop-w" value={c.width} onChange={e => { const n = this.num(e.target.value); if (n !== null) this.props.onState(setCropSize(state, { width: n })); }} /></div>
                    <div className={styles.num}><span>H</span><input inputMode="numeric" aria-label="Crop height" data-testid="crop-h" value={c.height} onChange={e => { const n = this.num(e.target.value); if (n !== null) this.props.onState(setCropSize(state, { height: n })); }} /></div>
                </div>
                <span className={styles.hint}>Drag a corner or an edge; drag inside to move. Arrow keys nudge.</span>
            </div>
            {batchCount > 1 && <div className={styles.row}>
                <label className={styles.l}>Apply to the batch</label>
                <div className={styles.chips}>
                    <button type="button" className={styles.chip} aria-pressed={cropPolicy === 'same-region'} onClick={() => this.props.onCropPolicy('same-region')}>Same region on same-size photos</button>
                    <button type="button" className={styles.chip} aria-pressed={cropPolicy === 'centre-aspect'} onClick={() => this.props.onCropPolicy('centre-aspect')}>Centre crop each</button>
                </div>
            </div>}
        </div>;
    }

    // ------------------------------------------------------------ rotate
    private rotatePane() {
        const { state } = this.props; const rv = rotateView(state);
        const fills: { v: CornerFill; label: string }[] = [{ v: 'transparent', label: 'Transparent' }, { v: 'white', label: 'White' }, { v: 'black', label: 'Black' }, { v: 'auto-crop', label: 'Auto-crop corners' }];
        return <div className={styles.pane}>
            {this.paneHead('rotate')}
            <div className={styles.row}>
                <label className={styles.l}>Quick turn</label>
                <div className={styles.btnrow}>
                    <button type="button" className={styles.btn} data-testid="rotate-left" onClick={() => this.props.onState(rotateQuarter(state, 'left'))}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7M3 3v6h6" /></svg>90° left</button>
                    <button type="button" className={styles.btn} data-testid="rotate-right" onClick={() => this.props.onState(rotateQuarter(state, 'right'))}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M21 12a9 9 0 1 1-3-6.7M21 3v6h-6" /></svg>90° right</button>
                </div>
            </div>
            <div className={styles.row}>
                <label className={styles.l} htmlFor="ed-angle">Fine angle</label>
                <div className={styles.angle}>
                    <input id="ed-angle" type="range" min={-45} max={45} step={0.5} value={rv.fineAngle} onChange={e => this.props.onState(setFineAngle(state, +e.target.value))}
                        onPointerDown={() => this.props.onAngleDragging(true)} onPointerUp={() => this.props.onAngleDragging(false)} onPointerCancel={() => this.props.onAngleDragging(false)}
                        onKeyDown={() => this.props.onAngleDragging(true)} onKeyUp={() => this.props.onAngleDragging(false)} onBlur={() => this.props.onAngleDragging(false)} />
                    <output>{rv.fineAngle}°</output>
                </div>
                <span className={styles.hint}>Straightens the horizon. Exposed corners take the fill below.</span>
            </div>
            <div className={styles.row}>
                <label className={styles.l}>Background for exposed corners</label>
                <div className={styles.chips}>{fills.map(f => <button key={f.v} type="button" className={styles.chip} aria-pressed={rv.cornerFill === f.v} onClick={() => this.props.onState(setCornerFill(state, f.v))}>{f.label}</button>)}</div>
                {rv.fineAngle !== 0 && rv.cornerFill === 'transparent' && !['png', 'webp', 'avif', 'gif', 'tiff', 'psd', 'ico'].includes(this.props.output.format) && <span className={styles.note}>{formatLabel(this.props.output.format)} has no transparency: the corners will be white in the saved file.</span>}
            </div>
        </div>;
    }

    // ------------------------------------------------------------ flip
    private flipPane() {
        const { state } = this.props; const fv = flipView(state);
        return <div className={styles.pane}>
            {this.paneHead('flip')}
            <div className={styles.row}>
                <label className={styles.l}>Mirror</label>
                <div className={styles.btnrow}>
                    <button type="button" className={styles.btn} aria-pressed={fv.h} data-testid="flip-h" onClick={() => this.props.onState(toggleFlip(state, 'horizontal'))}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v18M4 7l5 5-5 5V7zM20 7l-5 5 5 5V7z" /></svg>Horizontal{fv.h ? ' ✓' : ''}</button>
                    <button type="button" className={styles.btn} aria-pressed={fv.v} data-testid="flip-v" onClick={() => this.props.onState(toggleFlip(state, 'vertical'))}><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12h18M7 4l5 5 5-5H7zM7 20l5-5 5 5H7z" /></svg>Vertical{fv.v ? ' ✓' : ''}</button>
                </div>
                <span className={styles.hint}>Tap again to undo. Flips stack with rotate.</span>
            </div>
        </div>;
    }

    // ------------------------------------------------------------ output
    private outputBlock() {
        const { state, output, format, quality, batchCount, exporting, sourceFormat, canShare } = this.props;
        const single = batchCount <= 1;
        const lossy = LOSSY.includes(output.format);
        const original = state.source;
        const label = formatLabel(output.format);
        // Compact sheet (UX review M3/D2): the delta reads "(+1045%)" and may wrap instead of being
        // ellipsised; "Reset all" moved to the history trio over the photo; quality hides behind a
        // toggle on phones only (CSS). No unused `state` here — the trio owns the reset now.
        return <div className={styles.out}>
            <div className={styles.kv}>
                <span>Original</span><b>{original.width} × {original.height} · {formatLabel(sourceFormat)} · {this.props.originalBytes ? formatBytesShort(this.props.originalBytes) : '—'}</b>
                <span>Output</span><b data-testid="output-line">{single ? `${output.size.width} × ${output.size.height}` : `${batchCount} files`} · {label} · {output.estimate.replace('≈ ', '')}{output.delta && <> <span className={output.delta.up ? styles.up : styles.delta}>({output.delta.up ? '+' : '−'}{output.delta.pct}%)</span></>}</b>
            </div>
            <div className={styles.saveas}>
                {single
                    ? <button type="button" className={`${styles.btn} ${styles.primary}`} data-testid="download" disabled={!!exporting} title={single ? `Saved as ${output.filename}` : undefined} onClick={() => this.props.onExport('single')}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12M6 11l6 6 6-6M4 21h16" /></svg>{exporting ? exporting.label : `Download ${label}`}</button>
                    : <button type="button" className={`${styles.btn} ${styles.primary}`} data-testid="download-zip" disabled={!!exporting} onClick={() => this.props.onExport('zip')}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v12M6 11l6 6 6-6M4 21h16" /></svg>{exporting ? `${exporting.label} ${exporting.done}/${exporting.total}` : 'Download all (.zip)'}</button>}
                <select className={styles.select} aria-label="Save as" data-testid="save-as" value={format} onChange={e => this.props.onFormat(e.target.value as SaveFormat)}>
                    <option value="original">Same as original</option>
                    {SAVE_FORMATS.map(f => <option key={f} value={f}>{formatLabel(f)}</option>)}
                </select>
                {lossy && <button type="button" className={styles.qbtn} aria-pressed={this.state.showQuality} aria-label={`Quality ${quality} — show slider`} title={`Quality ${quality}`} onClick={() => this.setState(s => ({ showQuality: !s.showQuality }))}>
                    <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6" /></svg></button>}
            </div>
            {exporting && exporting.total > 1 && <div className={styles.progress} aria-hidden="true"><i style={{ width: `${Math.round(100 * exporting.done / exporting.total)}%` }} /></div>}
            {lossy && <div className={`${styles.quality} ${this.state.showQuality ? styles.show : ''}`}><span>Quality</span><input type="range" min={40} max={100} value={quality} aria-label="Quality" onChange={e => this.props.onQuality(+e.target.value)} /><output>{quality}</output></div>}
            {output.note && <div className={styles.note}>{output.note}</div>}
            {!single && !exporting && <button type="button" className={`${styles.btn} ${styles.ghost}`} onClick={() => this.props.onExport('each')}>Download one by one</button>}
            {single && canShare && !exporting && <button type="button" className={`${styles.btn} ${styles.ghost}`} data-testid="share" onClick={() => this.props.onExport('share')}>Share… (save to Photos, or send to an app)</button>}
            {single && COMPRESSIBLE.includes(output.format) && <a className={styles.link} href={`/compress-${output.format}`} data-testid="compress-link">Need a smaller file? Compress it →</a>}
            <span className={styles.hint}>Processed in your browser. Nothing is uploaded.</span>
            {single && <span className={styles.savedName} title={output.filename}>Saved as <b>{output.filename}</b></span>}
        </div>;
    }
}

function formatBytesShort(n: number): string {
    if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
    return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}
